import React, { useEffect, useRef, useState } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";

import api from "../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { CircularProgress } from "@mui/material";

function IframePage() {
    const [embedConfig, setEmbedConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const reportRef = useRef(null);
    /** "department" for DGP/ADGP/DIG report; "division" for everyone else (matches API filterType). */
    const powerBiFilterTypeRef = useRef("division");

    const getDivisionFilter = () => {
        const allowedDivisionIdsRaw = localStorage.getItem("allowedDivisionIds");
        if (allowedDivisionIdsRaw) {
            try {
                const parsedIds = JSON.parse(allowedDivisionIdsRaw);
                if (Array.isArray(parsedIds)) {
                    const divisionIds = parsedIds
                        .map((id) => (typeof id === "string" ? id.trim() : id))
                        .filter((id) => id !== null && id !== undefined && id !== "");

                    if (divisionIds.length > 0) {
                        return {
                            $schema: "http://powerbi.com/product/schema#basic",
                            target: {
                                table: process.env.REACT_APP_POWERBI_FILTER_DIVISION_ID_TABLE || "public division",
                                column: process.env.REACT_APP_POWERBI_FILTER_DIVISION_ID_COLUMN || "division_id",
                            },
                            operator: "In",
                            values: divisionIds,
                            filterType: models.FilterType.Basic,
                        };
                    }
                }
            } catch (e) {
                console.log("Invalid allowedDivisionIds format", e);
            }
        }

        const divisionValues = new Set();

        const selectedDivision = localStorage.getItem("division_name");
        if (selectedDivision && selectedDivision.trim()) {
            divisionValues.add(selectedDivision.trim());
        }

        const userOverallDesignationRaw = localStorage.getItem("userOverallDesignation");
        if (userOverallDesignationRaw) {
            try {
                const userOverallDesignation = JSON.parse(userOverallDesignationRaw);
                if (Array.isArray(userOverallDesignation)) {
                    userOverallDesignation.forEach((item) => {
                        const rawName = item?.division?.division_name || item?.division_name || item?.division;
                        if (typeof rawName === "string" && rawName.trim()) {
                            divisionValues.add(rawName.trim());
                        }
                    });
                }
            } catch (e) {
                console.log("Invalid userOverallDesignation format", e);
            }
        }

        const values = Array.from(divisionValues);
        if (values.length === 0) {
            return null;
        }

        return {
            $schema: "http://powerbi.com/product/schema#basic",
            target: {
                table: process.env.REACT_APP_POWERBI_FILTER_DIVISION_TABLE || "public division",
                column: process.env.REACT_APP_POWERBI_FILTER_DIVISION_COLUMN || "division_name",
            },
            operator: "In",
            values,
            filterType: models.FilterType.Basic,
        };
    };

    const getDepartmentFilter = () => {
        const allowedDepartmentIdsRaw = localStorage.getItem("allowedDepartmentIds");
        if (allowedDepartmentIdsRaw) {
            try {
                const parsedIds = JSON.parse(allowedDepartmentIdsRaw);
                if (Array.isArray(parsedIds)) {
                    const departmentIds = parsedIds
                        .map((id) => (typeof id === "string" ? id.trim() : id))
                        .filter((id) => id !== null && id !== undefined && id !== "");

                    if (departmentIds.length > 0) {
                        return {
                            $schema: "http://powerbi.com/product/schema#basic",
                            target: {
                                table: process.env.REACT_APP_POWERBI_FILTER_DEPARTMENT_ID_TABLE || "public department",
                                column: process.env.REACT_APP_POWERBI_FILTER_DEPARTMENT_ID_COLUMN || "department_id",
                            },
                            operator: "In",
                            values: departmentIds,
                            filterType: models.FilterType.Basic,
                        };
                    }
                }
            } catch (e) {
                console.log("Invalid allowedDepartmentIds format", e);
            }
        }

        const departmentValues = new Set();

        const selectedDepartment = localStorage.getItem("department_name");
        if (selectedDepartment && selectedDepartment.trim()) {
            departmentValues.add(selectedDepartment.trim());
        }

        const userOverallDesignationRaw = localStorage.getItem("userOverallDesignation");
        if (userOverallDesignationRaw) {
            try {
                const userOverallDesignation = JSON.parse(userOverallDesignationRaw);
                if (Array.isArray(userOverallDesignation)) {
                    userOverallDesignation.forEach((item) => {
                        const rawName =
                            item?.department?.department_name ||
                            item?.department_name ||
                            (typeof item?.department === "string" ? item.department : null);
                        if (typeof rawName === "string" && rawName.trim()) {
                            departmentValues.add(rawName.trim());
                        }
                    });
                }
            } catch (e) {
                console.log("Invalid userOverallDesignation format", e);
            }
        }

        const values = Array.from(departmentValues);
        if (values.length === 0) {
            return null;
        }

        return {
            $schema: "http://powerbi.com/product/schema#basic",
            target: {
                table: process.env.REACT_APP_POWERBI_FILTER_DEPARTMENT_TABLE || "public department",
                column: process.env.REACT_APP_POWERBI_FILTER_DEPARTMENT_COLUMN || "department_name",
            },
            operator: "In",
            values,
            filterType: models.FilterType.Basic,
        };
    };

    const getActivePowerBiFilter = () =>
        powerBiFilterTypeRef.current === "department" ? getDepartmentFilter() : getDivisionFilter();

    useEffect(() => {
        const callPowerBiUrl = async () => {
            setLoading(true);
            try {
                const designationName = localStorage.getItem("designation_name") || "";
                const response = await api.post("/powerBi/getEmbedToken", {
                    designation_name: designationName,
                });

                setLoading(false);

                if (response && response.success && response.data) {
                    const { data } = response;
                    const filterType = data.filterType === "department" ? "department" : "division";
                    powerBiFilterTypeRef.current = filterType;

                    const rowFilter = getActivePowerBiFilter();
                    const filters = rowFilter ? [rowFilter] : [];

                    setEmbedConfig({
                        type: "report",
                        id: data.reportId,
                        embedUrl: data.embedUrl,
                        accessToken: data.embedToken,
                        tokenType: models.TokenType.Embed,
                        settings: {
                            panes: {
                                filters: {
                                    expanded: false,
                                    visible: false,
                                },
                            },
                        },
                        filters,
                    });

                    return true;
                }
                toast.error(response?.["message"] ? response?.["message"] : "Please try again!", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    className: "toast-error",
                });
                return;
            } catch (error) {
                setLoading(false);
                if (error && error.response && error.response.data) {
                    toast.error(
                        error.response.data["message"] ? error.response.data["message"] : "Please try again!",
                        {
                            position: "top-right",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            className: "toast-error",
                        }
                    );
                }
            }
        };

        callPowerBiUrl();
    }, []);

    return (
        <div className="PowerBiApp">
            <header className="PowerBiAppHeader">
                {embedConfig && Object.keys(embedConfig).length > 0 && (
                    <PowerBIEmbed
                        embedConfig={embedConfig}
                        eventHandlers={
                            new Map([
                                [
                                    "loaded",
                                    async function () {
                                        console.log("Report loaded");
                                        try {
                                            const rowFilter = getActivePowerBiFilter();
                                            if (rowFilter && reportRef.current) {
                                                await reportRef.current.setFilters([rowFilter]);
                                            }
                                        } catch (filterError) {
                                            console.log("Error applying Power BI filters", filterError);
                                        }
                                    },
                                ],
                                [
                                    "rendered",
                                    function () {
                                        console.log("Report rendered");
                                    },
                                ],
                                [
                                    "error",
                                    function (event) {
                                        console.log(event.detail);
                                    },
                                ],
                            ])
                        }
                        cssClassName={"EmbedContainer"}
                        getEmbeddedComponent={(embeddedReport) => {
                            window.report = embeddedReport;
                            reportRef.current = embeddedReport;
                        }}
                    />
                )}
            </header>
            {loading && (
                <div className="parent_spinner" tabIndex="-1" aria-hidden="true">
                    <CircularProgress size={100} />
                </div>
            )}
        </div>
    );
}

export default IframePage;
